import { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

// Canonical group → doc types mapping used as a client-side fallback when
// /documents/stats does not return doc_type_groups. Group labels mirror the
// backend's DOC_FAMILY_LABEL (backend/config/doc_classification.py) —
// "Capital Calls" split out from "Distributions" so a client-call spelling
// never gets lumped in with distribution notices.
export const FALLBACK_GROUP_MAP = [
  {
    group: "Capital Calls",
    types: ["Capital Call"],
  },
  {
    group: "Distributions",
    types: [
      "Distribution Notice",
      "Dividend Confirmation",
      "Payment Confirmation",
    ],
  },
  {
    group: "NAV Reports",
    types: ["NAV Report", "Market Value Statement"],
  },
  {
    group: "Statements",
    types: [
      "Capital Account Statement",
      "Financial Statement",
      "Contract Note",
    ],
  },
  {
    group: "Tax Documents",
    types: ["Tax Document"],
  },
  {
    group: "Quarterly Reports",
    types: ["Quarterly Report", "Annual Report", "Investor Letter"],
  },
  {
    group: "Admin Documents",
    types: ["Subscription Agreement", "Regulatory Filing"],
  },
];

// Build a flat lookup: doc_type -> group name
export const FALLBACK_TYPE_TO_GROUP = {};
for (const { group, types } of FALLBACK_GROUP_MAP) {
  for (const t of types) {
    FALLBACK_TYPE_TO_GROUP[t] = group;
  }
}

// Given a list of {id, name} doc type options and an optional doc_type_groups
// array from /documents/stats, return groups in canonical order for the
// popover, only including types present in the options list.
export function buildGroups(docTypeOptions, docTypeGroups) {
  const availableIds = new Set(docTypeOptions.map((t) => t.id));

  // If the backend provided groups, use them (server canonical order).
  if (Array.isArray(docTypeGroups) && docTypeGroups.length > 0) {
    const sorted = [...docTypeGroups].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
    return sorted
      .map(({ group, types }) => ({
        group,
        items: (types || []).filter((t) => availableIds.has(t)).map((t) => ({ id: t, name: t })),
      }))
      .filter((g) => g.items.length > 0);
  }

  // Fallback: group using the client-side FALLBACK_GROUP_MAP.
  const ungrouped = [];
  const result = FALLBACK_GROUP_MAP.map(({ group, types }) => ({
    group,
    items: types
      .filter((t) => availableIds.has(t))
      .map((t) => ({ id: t, name: t })),
  })).filter((g) => g.items.length > 0);

  // Any types not in the fallback map go into "Other".
  for (const opt of docTypeOptions) {
    if (!FALLBACK_TYPE_TO_GROUP[opt.id]) {
      ungrouped.push({ id: opt.id, name: opt.name });
    }
  }
  if (ungrouped.length > 0) {
    result.push({ group: "Other", items: ungrouped });
  }

  // If no groups matched at all but we have docTypeOptions, render them flat
  // in a single "All types" group so nothing is lost.
  if (result.length === 0 && docTypeOptions.length > 0) {
    return [{ group: "All types", items: docTypeOptions }];
  }

  return result;
}

// ---------------------------------------------------------------------------
// DocTypeMultiSelect — grouped popover with cmdk search + checkboxes. Each
// group heading is itself a checkbox that selects/deselects EVERY type in the
// group ("Select all Statements"). Snapshot semantics: selection is the flat
// list of concrete doc-type ids — clicking a group simply expands to the types
// currently present in `docTypeOptions`.
//
// Shared by the Documents-page filter bar (DocumentFilters) and the Digest
// Rules editor (DigestRulesTab). `triggerClassName` lets each host size the
// trigger to its surrounding form.
// ---------------------------------------------------------------------------

const DEFAULT_TRIGGER_CLASS =
  "flex h-8 w-full items-center justify-between gap-1 rounded-md border border-input bg-background px-3 py-1 text-xs ring-offset-background hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";

export default function DocTypeMultiSelect({
  selected,
  onChange,
  docTypeOptions,
  docTypeGroups,
  triggerClassName = DEFAULT_TRIGGER_CLASS,
  placeholderLabel = "All types",
}) {
  const [open, setOpen] = useState(false);

  const groups = buildGroups(docTypeOptions, docTypeGroups);

  function toggle(id) {
    if (selected.includes(id)) {
      onChange(selected.filter((v) => v !== id));
    } else {
      onChange([...selected, id]);
    }
  }

  function toggleGroup(itemIds) {
    const allIn = itemIds.every((id) => selected.includes(id));
    if (allIn) {
      // Remove every item belonging to this group from the selection.
      onChange(selected.filter((v) => !itemIds.includes(v)));
    } else {
      // Add the missing items, preserving prior selection order.
      const missing = itemIds.filter((id) => !selected.includes(id));
      onChange([...selected, ...missing]);
    }
  }

  function clearAll(e) {
    e.preventDefault();
    onChange([]);
    setOpen(false);
  }

  const triggerLabel =
    selected.length === 0
      ? placeholderLabel
      : selected.length === 1
      ? selected[0]
      : `${selected.length} types`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="doc-type-trigger"
          className={triggerClassName}
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[240px] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder="Search types..." className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty className="text-xs py-4">No types found.</CommandEmpty>
            {groups.map(({ group, items }) => {
              const itemIds = items.map((i) => i.id);
              const selectedInGroup = itemIds.filter((id) =>
                selected.includes(id)
              );
              const allSelected =
                itemIds.length > 0 &&
                selectedInGroup.length === itemIds.length;
              const someSelected =
                selectedInGroup.length > 0 && !allSelected;

              const groupHeading = (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleGroup(itemIds);
                  }}
                  className="flex w-full items-center gap-2 cursor-pointer hover:text-foreground transition-colors"
                  data-testid={`doc-type-group-toggle-${group}`}
                  aria-label={
                    allSelected
                      ? `Deselect all ${group}`
                      : `Select all ${group}`
                  }
                >
                  <div
                    className={cn(
                      "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border border-primary",
                      allSelected
                        ? "bg-primary text-primary-foreground"
                        : someSelected
                        ? "bg-primary/40 text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible"
                    )}
                  >
                    <Check className="h-2.5 w-2.5" />
                  </div>
                  <span className="flex-1 text-left">{group}</span>
                  <span className="text-[10px] opacity-60 tabular-nums">
                    {selectedInGroup.length}/{itemIds.length}
                  </span>
                </button>
              );

              return (
                <CommandGroup key={group} heading={groupHeading}>
                  {items.map(({ id, name }) => {
                    const checked = selected.includes(id);
                    return (
                      <CommandItem
                        key={id}
                        value={id}
                        onSelect={() => toggle(id)}
                        className="flex items-center gap-2 text-xs"
                        data-testid={`doc-type-option-${id}`}
                      >
                        <div
                          className={cn(
                            "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                            checked
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50 [&_svg]:invisible"
                          )}
                        >
                          <Check className="h-3 w-3" />
                        </div>
                        <span className="flex-1 truncate">{name}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}
          </CommandList>
          {selected.length > 0 && (
            <div className="border-t p-1">
              <button
                type="button"
                onClick={clearAll}
                data-testid="doc-type-clear-popover"
                className="w-full rounded-sm px-2 py-1.5 text-center text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                Clear
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
